/* eslint-disable react-hooks/rules-of-hooks */
// https://github.com/remarkjs/react-markdown/tree/8.0.7
// https://www.markdownguide.org/basic-syntax/

import React from "react"
import Markdown from "react-markdown"
import { createStyles } from "@mantine/core"

import SegueLink from "app/static/frontend/middleware/SegueLink"
import { Icon } from "@/components/Icon"
import { useCopilotContext } from "@/components/CopilotAnalyze/Copilot.context"
import { useResponsiveSize } from "@/hooks"
import { useCopilotStorage } from "@/components/CopilotAnalyze/hooks/useCopilotStorage"

import { getIcon, parsePath } from "@/components/CopilotAnalyze/Message/helpers"
import isFeatureEnabled from "shared/featureflags/helperFunctions"

const useStyles = createStyles((theme) => ({
    richLink: {
        color: theme.colors.violet[7],

        ":hover": {
            textDecoration: "underline",
        },
        ":visited": {
            color: theme.colors.violet[5],
        },

        svg: {
            height: "0.75rem",
            verticalAlign: "unset",
        },
    },
}))

// Override individual components to customize the rendering of Markdown elements.
const components = {
    a: ({ children, href }) => {
        const { classes } = useStyles()
        const { setIsFullscreenOpen, setIsSessionsListOpen } = useCopilotContext()
        const { isMobile } = useResponsiveSize()
        const { setIsOpen } = useCopilotStorage()

        try {
            const { hostname, pathname, search } = new URL(href)

            // For internal links, we want to use our own SegueLink component to prevent a full page reload.
            if (hostname.includes("quorum.us")) {
                // Check if the link contains hybrid_search parameter
                const urlParams = new URLSearchParams(search)

                const isHybridSearchOnBillsEnabled = isFeatureEnabled("ff_hybrid_search_bills_page")
                if (!isHybridSearchOnBillsEnabled && urlParams.has("hybrid_search")) {
                    return null
                }

                // Extract the datatype from the path
                const { dataType } = parsePath(pathname)
                const icon = getIcon(dataType)

                return (
                    <SegueLink
                        to={`${pathname}${search}`}
                        optionalPreSegueFunction={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.stopPropagation()
                            setIsSessionsListOpen(false)
                            setIsFullscreenOpen(false)

                            // Close the main Copilot window on mobile devices when clicking links
                            if (isMobile) {
                                setIsOpen(false)
                            }
                        }}
                        className={classes.richLink}
                    >
                        {icon && <Icon icon={icon} />}
                        {children}
                    </SegueLink>
                )
            } else {
                // External links should open in a new tab.
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className={classes.richLink}>
                        <Icon icon="external-link" />
                        {children}
                    </a>
                )
            }
        } catch (error) {
            // The LLM is giving us garbage - pass here and it will render as plain text.
        }

        return <span>{children}</span>
    },
    img: ({ src, alt, title }) => {
        return <img src={src} alt={alt} title={title} style={{ maxWidth: "100%" }} />
    },
}

const Message = ({ children }) => {
    //@ts-expect-error
    return <Markdown components={components}>{children}</Markdown>
}

export default Message
